import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Notification, { NotificationType } from '../components/Notification';
import { supabase } from '../lib/supabase';

interface NotificationItem {
  id: string;
  type: NotificationType;
  message: string;
  timestamp: Date;
  read: boolean;
}

interface NotificationPreferences {
  [key: string]: boolean;
}

interface NotificationContextType {
  showNotification: (type: NotificationType, message: string) => void;
  notifications: NotificationItem[];
  clearNotifications: () => void;
  markAsRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  preferences: NotificationPreferences;
  updatePreferences: (key: string, value: boolean) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const DEFAULT_PREFERENCES = {
  booking_updates: true,
  payment_notifications: true,
  promotional_emails: false,
  service_reminders: true,
};

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationItem[]>(() => {
    const saved = localStorage.getItem('notifications');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((n: any) => ({
        ...n,
        timestamp: new Date(n.timestamp),
      }));
    }
    return [];
  });

  const [preferences, setPreferences] = useState<NotificationPreferences>(() => {
    const saved = localStorage.getItem('notificationPreferences');
    return saved ? JSON.parse(saved) : DEFAULT_PREFERENCES;
  });

  const [activeNotifications, setActiveNotifications] = useState<NotificationItem[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check initial auth state
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      if (!session) {
        // Clear notifications when user signs out
        setNotifications([]);
        setActiveNotifications([]);
        localStorage.removeItem('notifications');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('notifications', JSON.stringify(notifications));
    }
  }, [notifications, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
    }
  }, [preferences, isAuthenticated]);

  const showNotification = useCallback((type: NotificationType, message: string) => {
    if (!isAuthenticated) return; // Only show notifications if user is authenticated

    const id = Math.random().toString(36).substr(2, 9);
    const notification = {
      id,
      type,
      message,
      timestamp: new Date(),
      read: false,
    };

    setNotifications(prev => [notification, ...prev]);
    setActiveNotifications(prev => [...prev, notification]);

    setTimeout(() => {
      setActiveNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, [isAuthenticated]);

  const clearNotifications = useCallback(() => {
    if (!isAuthenticated) return;
    setNotifications([]);
  }, [isAuthenticated]);

  const markAsRead = useCallback((id: string) => {
    if (!isAuthenticated) return;
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, [isAuthenticated]);

  const deleteNotification = useCallback((id: string) => {
    if (!isAuthenticated) return;
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, [isAuthenticated]);

  const updatePreferences = useCallback((key: string, value: boolean) => {
    if (!isAuthenticated) return;
    setPreferences(prev => ({ ...prev, [key]: value }));
  }, [isAuthenticated]);

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        notifications,
        clearNotifications,
        markAsRead,
        deleteNotification,
        preferences,
        updatePreferences,
      }}
    >
      {children}
      {isAuthenticated && (
        <div className="fixed top-0 right-0 z-50 p-4 space-y-4 pointer-events-none">
          {activeNotifications.map(notification => (
            <Notification
              key={notification.id}
              type={notification.type}
              message={notification.message}
              onClose={() => {
                setActiveNotifications(prev =>
                  prev.filter(n => n.id !== notification.id)
                );
              }}
            />
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}